/**
 * @fileoverview A customizable bar chart component using Nivo charts
 * @requires react
 * @requires @nivo/bar
 * @requires antd
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { ResponsiveBar } from "@nivo/bar"
import { Select } from "antd"
import DataQButton from "../Buttons/DataQButton.component"
import { toPng } from "html-to-image"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChartColumn, faChartBar } from "@fortawesome/free-solid-svg-icons"
import { BarChartDataItem } from "utils/convertDataToTable"
import { chartColorScheme } from "constants/chartColorScheme"
import { BarChartTooltip } from "./CustomTooltip/CustomTooltip.component"
import { CustomSelect } from "./CustomSelect/CustomSelect.component"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"

const { Option } = Select

/**
 * Helper function to sort keys numerically
 */
const sortKeysNumerically = (keys: string[]): string[] => {
  return [...keys].sort((a: string, b: string): number => {
    // Handle "Totals" key - always put it last
    if (a === "Totals") return 1
    if (b === "Totals") return -1

    // Try to parse as numbers
    const numA: number = parseFloat(a)
    const numB: number = parseFloat(b)

    // If both are numbers, sort numerically
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB
    }

    // If only one is a number, number comes first
    if (!isNaN(numA)) return -1
    if (!isNaN(numB)) return 1

    // If neither is a number, sort alphabetically
    return a.localeCompare(b)
  })
}

/**
 * Detects the type of data structure we're working with
 */
const detectChartDataType = (
  data: BarChartDataItem[],
  keys: string[]
): "SIMPLE_AVERAGE" | "SIMPLE_VALUES" | "SIMPLE_TOTALS" | "CROSS_TAB" => {
  // DTAB data: Single "Average" key with avg values
  if (
    keys.length === 1 &&
    keys[0] === "Average" &&
    data.length > 0 &&
    data[0].Average &&
    typeof data[0].Average === "object" &&
    "avg" in (data[0].Average as object)
  ) {
    return "SIMPLE_AVERAGE"
  }

  // XTAB_SIMPLE data: Single "Values" key (new format)
  if (
    keys.length === 1 &&
    keys[0] === "Values" &&
    data.length > 0 &&
    data[0].Values &&
    typeof data[0].Values === "object"
  ) {
    return "SIMPLE_VALUES"
  }

  // XTAB_SIMPLE data: Single "Totals" key with count values
  if (
    keys.length === 1 &&
    keys[0] === "Totals" &&
    data.length > 0 &&
    data[0].Totals &&
    typeof data[0].Totals === "object"
  ) {
    return "SIMPLE_TOTALS"
  }

  // Multi-column cross-tabulation data
  return "CROSS_TAB"
}

/**
 * Props for the BarChart component
 */
interface BarChartProps {
  /** The data to display in the chart */
  data: BarChartDataItem[]
  /** The keys to use for the data */
  keys: string[]
  /** Available statistic types */
  statisticTypes?: STATISTIC_TYPE[]
  /** Label for the x-axis */
  xAxisLabel: string
  /** Whether to display as a chart */
  isChart: boolean
  /** Whether the chart is displayed in dashboard mode */
  isDashboard?: boolean
  /** Selected keys when in dashboard mode */
  dashboardSelectedKeys?: string[]
  /** Selected categories when in dashboard mode */
  dashboardSelectedCategories?: string[]
  /** Layout of the chart ("vertical" or "horizontal") */
  layout?: "vertical" | "horizontal"
  /** Whether to show totals */
  showTotals?: boolean
  /** Function to set show/hide totals */
  setShowTotals?: (show: boolean) => void
  /** Height of the chart container */
  height?: number
  /** Width of the chart container (used for image generation) */
  width?: number
}

/**
 * BarChart component for visualizing data
 */
const BarChart: React.FC<BarChartProps> = ({
  data,
  keys,
  statisticTypes = [],
  xAxisLabel,
  isChart,
  isDashboard = false,
  dashboardSelectedKeys = [],
  dashboardSelectedCategories = [],
  layout = "vertical",
  showTotals = true,
  setShowTotals = () => {},
  height,
  width,
}) => {
  const [selectedStatistic, setSelectedStatistic] =
    useState<STATISTIC_TYPE | null>(null)
  const [chartHeight, setChartHeight] = useState<number>(400)
  const [leftMargin, setLeftMargin] = useState<number>(60)
  const [bottomMargin, setBottomMargin] = useState<number>(120)
  const [chartLayout, setChartLayout] = useState<"vertical" | "horizontal">(
    layout
  )

  // Detect the type of chart data we're working with
  const chartDataType = useMemo(
    () => detectChartDataType(data, keys),
    [data, keys]
  )

  const isSimpleData =
    chartDataType === "SIMPLE_AVERAGE" ||
    chartDataType === "SIMPLE_VALUES" ||
    chartDataType === "SIMPLE_TOTALS"

  // Sort keys numerically for consistent ordering
  const sortedKeys: string[] = useMemo(() => {
    if (isSimpleData) {
      if (chartDataType === "SIMPLE_AVERAGE") return ["Average"]
      if (chartDataType === "SIMPLE_VALUES") return ["Values"]
      if (chartDataType === "SIMPLE_TOTALS") return ["Totals"]
    }
    return sortKeysNumerically(keys)
  }, [keys, isSimpleData, chartDataType])

  const [selectedKeys, setSelectedKeys] = useState<string[]>(
    isSimpleData
      ? [sortedKeys[0]]
      : sortedKeys.filter((key: string) => key !== "Totals")
  )
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    data
      .filter((item: BarChartDataItem) => item.xValue !== "Totals")
      .map((item: BarChartDataItem) => String(item.xValue))
  )

  const chartRef = useRef<HTMLDivElement>(null)

  // Dynamic legend spacing calculation
  const calculateResponsiveLegendSpacing = useCallback(() => {
    if (isSimpleData) return 30 // No legend for simple data

    // Filter out special keys to get actual breakdown variables
    const actualBreakdownKeys = sortedKeys.filter(
      (key) => key !== "Totals" && key !== "Average" && key !== "Total"
    )

    if (actualBreakdownKeys.length === 0) return 30

    const keyCount = actualBreakdownKeys.length
    const maxKeyLength = Math.max(
      ...actualBreakdownKeys.map((key) => key.length),
      0
    )

    // Reduced spacing calculation
    const baseSpacing = 50 // Reduced from 80
    const keyLengthFactor = Math.max(maxKeyLength * 4, 50) // Reduced from 7px to 4px per character
    const keyCountFactor = Math.min(keyCount * 8, 30) // Reduced from 12px to 8px per key, max 30px

    // Reduced layout factor
    const layoutFactor = chartLayout === "horizontal" ? 10 : 0 // Reduced from 20 to 10

    const totalSpacing =
      baseSpacing + keyLengthFactor + keyCountFactor + layoutFactor

    // Reduced maximum cap
    return Math.min(totalSpacing, 200) // Reduced from 320 to 200
  }, [sortedKeys, isSimpleData, chartLayout])

  // Set initial statistic when statisticTypes changes
  useEffect(() => {
    if (statisticTypes.length > 0 && !selectedStatistic) {
      if (
        chartDataType === "SIMPLE_AVERAGE" &&
        statisticTypes.includes(STATISTIC_TYPE.AVG)
      ) {
        setSelectedStatistic(STATISTIC_TYPE.AVG)
      } else if (
        chartDataType === "SIMPLE_TOTALS" &&
        statisticTypes.includes(STATISTIC_TYPE.N)
      ) {
        setSelectedStatistic(STATISTIC_TYPE.N)
      } else {
        setSelectedStatistic(statisticTypes[0])
      }
    }
  }, [statisticTypes, selectedStatistic, chartDataType])

  // Update selectedKeys when we detect simple data
  useEffect(() => {
    if (isSimpleData) {
      setSelectedKeys([sortedKeys[0]])
    } else if (!isDashboard) {
      setSelectedKeys(sortedKeys.filter((key: string) => key !== "Totals"))
    }
  }, [isSimpleData, sortedKeys, isDashboard])

  // Update layout when the layout prop changes
  useEffect(() => {
    setChartLayout(layout)
  }, [layout])

  // Apply dashboard selections when they change
  useEffect(() => {
    if (isDashboard && !isSimpleData) {
      if (dashboardSelectedKeys.length > 0) {
        setSelectedKeys(dashboardSelectedKeys)
      }
      if (dashboardSelectedCategories.length > 0) {
        setSelectedCategories(dashboardSelectedCategories)
      }
    }
  }, [
    isDashboard,
    dashboardSelectedKeys,
    dashboardSelectedCategories,
    isSimpleData,
  ])

  /**
   * Formats tick values based on the selected statistic type
   */
  const formatTickValue = useCallback(
    (value: number | null | undefined | string): string => {
      // Handle non-numeric values
      if (value == null) return "0"
      if (typeof value === "string") return value
      if (typeof value !== "number") return String(value)
      if (value === 0) return "0"
      if (!selectedStatistic) return value.toLocaleString()

      const statisticString = String(selectedStatistic)
      if (
        statisticString.toLowerCase().includes("perc") ||
        statisticString.toLowerCase().includes("percent") ||
        statisticString.toLowerCase().includes("%")
      ) {
        return `${(value * 100).toFixed(2)}%`
      }

      // For averages, show decimal places
      if (statisticString === "avg") {
        return value.toFixed(2)
      }

      return value.toLocaleString()
    },
    [selectedStatistic]
  )

  /**
   * Updates chart dimensions based on data and selections
   */
  useEffect(() => {
    if (!selectedStatistic) return

    const calculateChartHeight = (): number => {
      const baseHeight = 400

      if (chartLayout === "horizontal") {
        const categoryCount = selectedCategories.length
        return baseHeight + Math.max(0, (categoryCount - 5) * 30)
      } else {
        const xLabelLength = Math.max(
          ...selectedCategories.map((item: string) => item.length)
        )
        const xItemCount = selectedCategories.length
        const additionalHeight =
          Math.max(xLabelLength * 2 - 20, 0) + Math.max(xItemCount * 10 - 50, 0)
        return baseHeight + additionalHeight
      }
    }

    setChartHeight(calculateChartHeight())

    if (chartLayout === "horizontal") {
      const longestCategoryLabel = Math.max(
        ...selectedCategories.map((cat: string) => cat.length)
      )
      const newLeftMargin = Math.max(80, longestCategoryLabel * 8)
      setLeftMargin(newLeftMargin)
      setBottomMargin(70)
    } else {
      const maxValue = Math.max(
        ...data.flatMap((item: BarChartDataItem) =>
          Object.values(item)
            .filter(
              (val) =>
                typeof val === "object" && val && selectedStatistic in val
            )
            .map(
              (val) =>
                (val as Record<string, number>)[String(selectedStatistic)] || 0
            )
        )
      )
      const longestYLabel = formatTickValue(maxValue)
      const newLeftMargin = Math.max(60, longestYLabel.length * 8 + 40)
      setLeftMargin(newLeftMargin)
      setBottomMargin(120)
    }
  }, [
    data,
    selectedStatistic,
    selectedCategories,
    formatTickValue,
    chartLayout,
  ])

  // Configure margins based on layout and dynamic legend spacing (moved before early returns)
  const chartMargins = useMemo(
    () => ({
      top: 50,
      right: calculateResponsiveLegendSpacing(),
      bottom: bottomMargin,
      left: leftMargin,
    }),
    [calculateResponsiveLegendSpacing, bottomMargin, leftMargin]
  )

  if (!data || data.length === 0 || sortedKeys.length === 0) {
    return <div className="mt-5">No data available for the chart</div>
  }

  if (!isChart || !statisticTypes.length || !selectedStatistic) {
    return (
      <div className="mt-5">
        Unable to display this output as a chart. Please view as table.
      </div>
    )
  }

  // For simple data, we don't need to filter by totals the same way
  const chartKeys: string[] = isSimpleData
    ? [sortedKeys[0]] // Use the single key (Average or Totals)
    : showTotals
    ? sortedKeys
    : sortedKeys.filter((key: string) => key !== "Totals")

  /**
   * Prepares chart data based on selected categories and keys
   */
  const chartData: Record<string, string | number>[] = data
    .filter((item: BarChartDataItem) => {
      // For simple data, show all categories except Totals (unless it's simple totals and showTotals is true)
      if (isSimpleData) {
        if (chartDataType === "SIMPLE_TOTALS") {
          return item.xValue !== "Totals" || showTotals
        }
        return true // For simple averages, show all
      }

      // For cross-tabulation data, use existing logic
      if (isDashboard && dashboardSelectedCategories.length > 0) {
        return (
          dashboardSelectedCategories.includes(String(item.xValue)) ||
          (showTotals && item.xValue === "Totals")
        )
      } else {
        return (
          selectedCategories.includes(String(item.xValue)) ||
          (showTotals && item.xValue === "Totals")
        )
      }
    })
    .map((item: BarChartDataItem) => {
      const newItem: Record<string, string | number> = {
        xValue: String(item.xValue),
      }

      if (chartDataType === "SIMPLE_AVERAGE") {
        // For simple average data, directly use the average value
        const averageData = item.Average as { avg: number }
        newItem.Average = averageData.avg || 0
      } else if (chartDataType === "SIMPLE_VALUES") {
        // For simple values data, use the values directly
        const valuesData = item.Values as { [key: string]: number }
        const statType = String(selectedStatistic)
        newItem.Values = valuesData[statType] || 0
      } else if (chartDataType === "SIMPLE_TOTALS") {
        // For simple totals data, use the totals value
        const totalsData = item.Totals as { [key: string]: number }
        const statType = String(selectedStatistic)
        newItem.Totals = totalsData[statType] || 0
      } else {
        // For cross-tabulation data, use existing logic
        sortedKeys.forEach((key: string) => {
          if (
            (isDashboard && dashboardSelectedKeys.length > 0
              ? dashboardSelectedKeys.includes(key)
              : selectedKeys.includes(key)) ||
            (showTotals && key === "Totals")
          ) {
            const itemValue = item[key]
            if (
              itemValue &&
              typeof itemValue === "object" &&
              selectedStatistic in itemValue
            ) {
              newItem[key] =
                (itemValue as Record<string, number>)[
                  String(selectedStatistic)
                ] || 0
            } else {
              newItem[key] = 0
            }
          }
        })
      }
      return newItem
    })

  /**
   * Handles statistic type selection change
   */
  const handleStatisticChange = (value: STATISTIC_TYPE): void => {
    setSelectedStatistic(value)
  }

  /**
   * Handles keys selection change
   */
  const handleKeysChange = (values: string[]): void => {
    if (!isSimpleData) {
      setSelectedKeys(values)
    }
  }

  /**
   * Handles categories selection change
   */
  const handleCategoriesChange = (values: string[]): void => {
    setSelectedCategories(values)
  }

  /**
   * Handles layout change toggle
   */
  const toggleLayout = (): void => {
    setChartLayout(chartLayout === "vertical" ? "horizontal" : "vertical")
  }

  /**
   * Exports the chart as a PNG image
   */
  const exportChart = (): void => {
    if (chartRef.current) {
      toPng(chartRef.current, { quality: 0.95 })
        .then((dataUrl: string) => {
          const link = document.createElement("a")
          link.download = "chart.png"
          link.href = dataUrl
          link.click()
        })
        .catch((err: Error) => {
          console.error("Error exporting chart:", err)
        })
    }
  }

  // Configure axis based on layout
  const axisBottom =
    chartLayout === "horizontal"
      ? {
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: String(selectedStatistic),
          legendPosition: "middle" as const,
          legendOffset: 45,
          format: formatTickValue,
        }
      : {
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 45,
          legend: xAxisLabel || "Categories",
          legendPosition: "middle" as const,
          legendOffset: 80,
        }

  const axisLeft =
    chartLayout === "horizontal"
      ? {
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: xAxisLabel || "Categories",
          legendPosition: "middle" as const,
          legendOffset: -leftMargin + 20,
        }
      : {
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: String(selectedStatistic),
          legendPosition: "middle" as const,
          legendOffset: -leftMargin + 20,
          format: formatTickValue,
        }

  return (
    <div className="w-full mt-5">
      {/* Only show controls when not in dashboard mode */}
      {!isDashboard && (
        <div className="flex flex-wrap items-end gap-4 mb-5">
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-semibold">Statistic Type</label>
            <Select
              className="w-48"
              value={selectedStatistic || undefined}
              onChange={handleStatisticChange}
              disabled={isSimpleData} // Disable for simple data since there's only one option
            >
              {statisticTypes.map((type: STATISTIC_TYPE) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </div>

          {/* Only show variable selection for cross-tabulation data */}
          {!isSimpleData && (
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-semibold">Variables</label>
              <CustomSelect
                placeholder="Select variables"
                value={selectedKeys}
                onChange={handleKeysChange}
                options={sortedKeys
                  .filter((key: string) => key !== "Totals")
                  .map((key: string) => ({
                    value: key,
                    label: key,
                  }))}
              />
            </div>
          )}

          <div className="flex flex-col">
            <label className="mb-1 text-sm font-semibold">Categories</label>
            <CustomSelect
              placeholder="Select categories"
              value={selectedCategories}
              onChange={handleCategoriesChange}
              options={data
                .filter(
                  (item: BarChartDataItem) =>
                    isSimpleData || item.xValue !== "Totals"
                )
                .map((item: BarChartDataItem) => ({
                  value: String(item.xValue),
                  label: String(item.xValue),
                }))}
            />
          </div>

          <div className="flex gap-2">
            <DataQButton type="primary" onClick={exportChart}>
              Export
            </DataQButton>
            <DataQButton onClick={toggleLayout}>
              <FontAwesomeIcon
                icon={chartLayout === "vertical" ? faChartBar : faChartColumn}
              />
              {chartLayout === "vertical" ? "Horizontal" : "Vertical"}
            </DataQButton>
          </div>

          {/* Only show totals toggle for cross-tabulation data or simple totals */}
          {(!isSimpleData || chartDataType === "SIMPLE_TOTALS") && (
            <div className="flex items-center ml-4">
              <input
                type="checkbox"
                id="show-totals"
                checked={showTotals}
                onChange={() => setShowTotals(!showTotals)}
                className="mr-2"
              />
              <label htmlFor="show-totals" className="text-sm">
                Show Totals
              </label>
            </div>
          )}
        </div>
      )}

      <div
        className="max-w-full overflow-auto"
        style={{
          height: `${height || chartHeight}px`,
          width: width ? `${width}px` : undefined,
        }}
        ref={chartRef}
      >
        <ResponsiveBar
          data={chartData}
          keys={chartKeys}
          indexBy="xValue"
          margin={chartMargins}
          padding={0.3}
          layout={chartLayout}
          groupMode="grouped"
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          colors={(datum: {
            id: string | number
            indexValue?: string | number
          }): string => {
            if (isSimpleData) {
              // For simple data, color by category (indexValue) instead of series (id)
              const categoryIndex = chartData.findIndex(
                (item) => String(item.xValue) === String(datum.indexValue)
              )
              return chartColorScheme[categoryIndex % chartColorScheme.length]
            }
            const keyIndex: number = sortedKeys.indexOf(String(datum.id))
            return chartColorScheme[keyIndex % chartColorScheme.length]
          }}
          borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={axisBottom}
          axisLeft={axisLeft}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          legends={
            isSimpleData
              ? []
              : [
                  // Hide legend for simple data charts
                  {
                    dataFrom: "keys" as const,
                    anchor: "bottom-right" as const,
                    direction: "column" as const,
                    justify: false,
                    translateX: 120,
                    translateY: 0,
                    itemsSpacing: 2,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemDirection: "left-to-right" as const,
                    itemOpacity: 0.85,
                    symbolSize: 20,
                    effects: [
                      {
                        on: "hover" as const,
                        style: {
                          itemOpacity: 1,
                        },
                      },
                    ],
                  },
                ]
          }
          animate={true}
          theme={{
            axis: {
              ticks: {
                text: {
                  fill: "#333333",
                },
              },
              legend: {
                text: {
                  fill: "#333333",
                  fontSize: 14,
                },
              },
            },
            legends: {
              text: {
                fill: "#333333",
              },
            },
          }}
          tooltip={({
            id,
            value,
            color,
            indexValue,
          }: {
            id: string | number
            value: number
            color: string
            indexValue: string | number
          }) => (
            <BarChartTooltip
              id={String(id)}
              value={formatTickValue(value)}
              color={color}
              indexValue={String(indexValue)}
            />
          )}
        />
      </div>
    </div>
  )
}

export default BarChart
